import { useEffect, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Badge } from "@/components/ui/badge";
import { Download, Loader2, MoreHorizontal } from "lucide-react";
import type { ProductEditPageProduct } from "@/routes/productEditTypes";

interface Recording {
  id: string;
  download_url?: string;
  status?: string;
  invoked_time?: string;
  started_time?: string;
  recording_duration?: number;
  file_size?: number;
}

function formatDate(dateString?: string) {
  if (!dateString) return "—";
  const date = new Date(dateString);
  return date.toLocaleString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function formatDuration(seconds?: number) {
  if (seconds == null) return "—";
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  if (mins === 0) return `${secs}s`;
  return `${mins}m ${secs}s`;
}

function formatFileSize(bytes?: number) {
  if (bytes == null) return "—";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function statusBadgeClass(status?: string) {
  switch (status) {
    case "UPLOADED":
      return "bg-green-500 text-white";
    case "RECORDING":
    case "UPLOADING":
      return "bg-blue-500 text-white";
    case "ERRORED":
      return "bg-red-500 text-white";
    default:
      return "bg-gray-500 text-white";
  }
}

export function ProductEditRecordingsTab({
  productId,
  product,
}: {
  productId: string;
  product: ProductEditPageProduct;
}) {
  const [recordings, setRecordings] = useState<Recording[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const meetingId = product.meeting?.realtimeKit?.meetingId;

  useEffect(() => {
    async function fetchRecordings() {
      if (!meetingId) {
        setRecordings([]);
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        const response = await fetch(
          `/api/products/${productId}/meeting/recordings`
        );
        const data = await response.json();

        if (!response.ok) {
          setError(data.error || "Failed to fetch recordings");
          setRecordings([]);
          return;
        }

        setRecordings(data.recordings || []);
      } catch (fetchError) {
        console.error("Failed to fetch recordings:", fetchError);
        setError("An error occurred while fetching recordings");
        setRecordings([]);
      } finally {
        setIsLoading(false);
      }
    }

    void fetchRecordings();
  }, [productId, meetingId]);

  if (!meetingId) {
    return (
      <p className="text-sm text-muted-foreground">
        No meeting configured for this product.
      </p>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />
        Loading recordings...
      </div>
    );
  }

  if (error) {
    return <p className="text-sm text-destructive">{error}</p>;
  }

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Status</TableHead>
          <TableHead>Started</TableHead>
          <TableHead>Duration</TableHead>
          <TableHead>Size</TableHead>
          <TableHead className="w-[50px]" />
        </TableRow>
      </TableHeader>
      <TableBody>
        {recordings.length > 0 ? (
          recordings.map((recording) => (
            <TableRow key={recording.id}>
              <TableCell>
                <Badge className={statusBadgeClass(recording.status)}>
                  {recording.status || "Unknown"}
                </Badge>
              </TableCell>
              <TableCell>
                {formatDate(recording.started_time || recording.invoked_time)}
              </TableCell>
              <TableCell>{formatDuration(recording.recording_duration)}</TableCell>
              <TableCell>{formatFileSize(recording.file_size)}</TableCell>
              <TableCell>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon">
                      <MoreHorizontal className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem
                      disabled={!recording.download_url}
                      onClick={() => {
                        if (recording.download_url) {
                          window.open(
                            recording.download_url,
                            "_blank",
                            "noopener,noreferrer"
                          );
                        }
                      }}
                    >
                      <Download className="h-4 w-4 mr-2" />
                      Download
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </TableCell>
            </TableRow>
          ))
        ) : (
          <TableRow>
            <TableCell colSpan={5} className="h-24 text-center">
              No recordings found.
            </TableCell>
          </TableRow>
        )}
      </TableBody>
    </Table>
  );
}
